import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type IUserShortcutProps,
    NCoreUIKitTheme,
    UserShortcut,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";
import {
    UserIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "UserShortcut";

const USAGE_CODE = `const [isSignedIn, setIsSignedIn] = useState(true);

<UserShortcut
    actionButtons={[
        {
            onPress: openProfile,
            title: "My profile",
            icon: ProfileIcon
        },
        {
            onPress: openSettings,
            icon: SettingsIcon,
            title: "Settings"
        }
    ]}
    avatarProps={{
        statusIndicatorType: "online",
        title: "Ada Lovelace"
    }}
    onLogoutPress={() => setIsSignedIn(false)}
    onLoginPress={() => setIsSignedIn(true)}
    isSessionActive={isSignedIn}
    isWorkWithCoreComplex={false}
    email="ada@example.com"
    subTitle="Administrator"
    title="Ada Lovelace"
/>`;

const getItemStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingMd,
    alignItems: "center",
    display: "flex"
});

const getDrawerStyle = ({
    shapeConfigs
}: DocsStyleTokens): CSSProperties => ({
    width: shapeConfigs.drawerWidth,
    maxWidth: "100%"
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("userShortcutPage.statusTitle"),
        id: "status"
    },
    {
        title: t("userShortcutPage.signedOutTitle"),
        id: "signed-out"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const UserShortcutPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isSessionActive,
        setIsSessionActive
    ] = useState(true);

    const handleLogin = () => setIsSessionActive(true);

    const handleLogout = () => setIsSessionActive(false);

    const actionButtons: IUserShortcutProps["actionButtons"] = [
        {
            onPress: () => undefined,
            title: t("sample.item1"),
            icon: UserIcon
        },
        {
            title: t("userShortcutPage.notifications"),
            onPress: () => undefined,
            icon: BellIcon
        }
    ];

    const people = [
        {
            name: t("sample.name"),
            status: "online"
        },
        {
            name: t("userShortcutPage.person2"),
            status: "busy"
        },
        {
            name: t("userShortcutPage.person3"),
            status: "away"
        },
        {
            name: t("userShortcutPage.person4"),
            status: "offline"
        }
    ] as const;

    const renderUser = (props: IUserShortcutProps) => <UserShortcut
        subTitle={t("userShortcutPage.role")}
        avatarProps={{
            statusIndicatorType: "online",
            title: t("sample.name")
        }}
        actionButtons={actionButtons}
        isWorkWithCoreComplex={false}
        title={t("sample.name")}
        spreadBehaviour="free"
        {...props}
    />;

    const renderItem = (label: string, node: ReactNode) => <div
        style={getItemStyle(docsTokens)}
        key={label}
    >
        {node}
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("userShortcutPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("userShortcutPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("userShortcutPage.overviewCaption")}
            >
                {
                    renderUser({
                        email: t("userShortcutPage.email"),
                        onLogoutPress: handleLogout,
                        onLoginPress: handleLogin,
                        isSessionActive
                    })
                }
            </Preview>
            <Callout>
                {t("userShortcutPage.coreComplexNote")}
            </Callout>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("userShortcutPage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    renderItem("variant=\"default\"", renderUser({
                        email: t("userShortcutPage.email"),
                        variant: "default"
                    }))
                }
                {
                    renderItem("variant=\"compact\"", renderUser({
                        variant: "compact"
                    }))
                }
                {
                    renderItem("variant=\"drawer\"", <div
                        style={getDrawerStyle(docsTokens)}
                    >
                        <DemoFrame>
                            {
                                renderUser({
                                    email: t("userShortcutPage.email"),
                                    variant: "drawer"
                                })
                            }
                        </DemoFrame>
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("userShortcutPage.statusTitle")}
            id="status"
        >
            <Paragraph>
                {t("userShortcutPage.statusText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    people.map((person) => <UserShortcut
                        avatarProps={{
                            statusIndicatorType: person.status,
                            title: person.name
                        }}
                        subTitle={t("userShortcutPage.role")}
                        isWorkWithCoreComplex={false}
                        spreadBehaviour="stretch"
                        isShowArrowButton={false}
                        title={person.name}
                        key={person.name}
                    />)
                }
                <UserShortcut
                    statusText={t("userShortcutPage.inMeeting")}
                    avatarProps={{
                        title: t("userShortcutPage.person5"),
                        statusIndicatorType: "busy"
                    }}
                    subTitle={t("userShortcutPage.role")}
                    title={t("userShortcutPage.person5")}
                    isWorkWithCoreComplex={false}
                    spreadBehaviour="stretch"
                    isShowArrowButton={false}
                />
            </Preview>
        </Section>
        <Section
            title={t("userShortcutPage.signedOutTitle")}
            id="signed-out"
        >
            <Paragraph>
                {t("userShortcutPage.signedOutText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <UserShortcut
                    onRegisterPress={() => undefined}
                    onLoginPress={() => undefined}
                    isWorkWithCoreComplex={false}
                    spreadBehaviour="stretch"
                    isSessionActive={false}
                />
                <UserShortcut
                    onRegisterPress={() => undefined}
                    onLoginPress={() => undefined}
                    isWorkWithCoreComplex={false}
                    isShowRegisterButton={true}
                    spreadBehaviour="stretch"
                    isSessionActive={false}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("userShortcutPage.statesText")}
            </Paragraph>
            <Preview>
                {
                    renderItem("isDisabled", renderUser({
                        isDisabled: true
                    }))
                }
                {
                    renderItem("isShowArrowButton={false}", renderUser({
                        isShowArrowButton: false
                    }))
                }
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("userShortcutPage.a11yTrigger"),
                    t("userShortcutPage.a11yExpanded"),
                    t("userShortcutPage.a11yMenu"),
                    t("userShortcutPage.a11yHidden"),
                    t("userShortcutPage.a11yDisabled"),
                    t("userShortcutPage.a11yFocus")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("userShortcutPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("userShortcutPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("userShortcutPage.propEmail"),
                        type: "string",
                        name: "email"
                    },
                    {
                        description: t("userShortcutPage.propStatusText"),
                        name: "statusText",
                        type: "string"
                    },
                    {
                        description: t("userShortcutPage.propStatusColor"),
                        type: "keyof TextContentColors",
                        name: "statusColor"
                    },
                    {
                        description: t("userShortcutPage.propAvatarProps"),
                        name: "avatarProps",
                        type: "IAvatarProps"
                    },
                    {
                        description: t("userShortcutPage.propBadges"),
                        type: "Array<ReactNode>",
                        name: "badges"
                    },
                    {
                        description: t("userShortcutPage.propVariant"),
                        type: "\"default\" | \"compact\" | \"drawer\"",
                        defaultValue: "\"default\"",
                        name: "variant"
                    },
                    {
                        description: t("userShortcutPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("userShortcutPage.propBackgroundColor"),
                        type: "keyof ContainerContentColors | \"transparent\"",
                        name: "backgroundColor"
                    },
                    {
                        description: t("userShortcutPage.propMaxWidth"),
                        type: "DimensionValue",
                        name: "maxWidth"
                    },
                    {
                        description: t("userShortcutPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsSessionActive"),
                        name: "isSessionActive",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsWorkWithCoreComplex"),
                        name: "isWorkWithCoreComplex",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsShowArrowButton"),
                        name: "isShowArrowButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsShowLogoutButton"),
                        name: "isShowLogoutButton",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsCloseBottomSheetOnLogoutPress"),
                        name: "isCloseBottomSheetOnLogoutPress",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsShowLoginButtonWhenNoSession"),
                        name: "isShowLoginButtonWhenNoSession",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("userShortcutPage.propIsShowRegisterButton"),
                        name: "isShowRegisterButton",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "Array<IButtonProps & { isCloseBottomSheetOnPress?: boolean }>",
                        description: t("userShortcutPage.propActionButtons"),
                        name: "actionButtons"
                    },
                    {
                        description: t("userShortcutPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("userShortcutPage.propOnLoginPress"),
                        name: "onLoginPress",
                        type: "() => void"
                    },
                    {
                        description: t("userShortcutPage.propOnRegisterPress"),
                        name: "onRegisterPress",
                        type: "() => void"
                    },
                    {
                        type: "(response?: unknown, error?: unknown) => void",
                        description: t("userShortcutPage.propOnLogoutPress"),
                        name: "onLogoutPress"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("userShortcutPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("userShortcutPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("userShortcutPage.propStyle"),
                        type: "StyleProp<ViewStyle>",
                        name: "style"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default UserShortcutPage;
